/**
 * What you have asked for, and where it got to (WALLIYO-0131).
 *
 * It sits on your own profile beside the form that asks, because a request
 * you cannot follow is one you will ask for again. It needs no feature, for
 * the same reason asking needs none: this is your own access.
 *
 * Deliberately not the Cases screen. That is gated on the cases feature, so
 * an engineer who can ask would have had nowhere to look — which was the
 * whole gap.
 */

import type { MyAccessRequest } from "@wallyo/contracts/console/cases";

import { listMyAccessRequests } from "../../api/console-api";
import { useApi } from "../../api/context";
import { permissionName, t } from "../../i18n";
import { formatDate } from "../../i18n/format";
import { LoadFailed, Loading, useLoad } from "../config-parts";

const TONES: Record<MyAccessRequest["state"], string> = {
  waiting: "warn",
  granted: "ok",
  refused: "bad",
};

export function MyRequests() {
  const { client } = useApi();
  const list = useLoad(() => listMyAccessRequests(client), "my-access-requests");

  if (list.state.status === "failed") {
    return (
      <LoadFailed
        message={t("access.mine.loadFailed")}
        retry={t("teams.retry")}
        error={list.state.error}
        onRetry={list.reload}
      />
    );
  }
  if (!list.state.data) return <Loading text={t("access.mine.loading")} />;

  const requests = list.state.data.requests;
  if (requests.length === 0) return null;

  return (
    <section className="card">
      <h2>{t("access.mine.title")}</h2>
      <table className="config-list feature-table">
        <thead>
          <tr>
            <th>{t("access.mine.what")}</th>
            <th>{t("access.mine.state")}</th>
            <th>{t("access.mine.asked")}</th>
            <th>{t("access.mine.withWhom")}</th>
            <th>{t("access.mine.answer")}</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((request) => (
            <tr key={request.supportCaseId}>
              <td>
                {permissionName(request.settingId as never)}
                <br />
                <span className="mono meta">{request.reference}</span>
              </td>
              <td>
                <span className={`pill ${TONES[request.state]}`}>{t(`access.mine.${request.state}` as const)}</span>
                {/* Only while it waits: a request answered late is answered. */}
                {request.overdue ? <span className="value-note unsaved">{t("access.mine.overdue")}</span> : null}
              </td>
              <td className="mono">{formatDate(request.askedAt, { dateStyle: "short" })}</td>
              <td>{request.withWhom ?? <span className="meta">{t("access.mine.nobody")}</span>}</td>
              {/* The refusal is the part worth reading: it says what to do instead. */}
              <td>{request.answer ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
